import { memo, useEffect, useRef, useState } from 'react'
import { getScoreUrl } from '../../services/api'

/* The score animation box under the game header — every sport, cricket
 * included (cricket ALSO keeps its socket scorecard in the sidebar).
 *
 * Reference (admin bundle `admin/layout.html`, game-details page):
 *
 *   gameDetail && 0 != gameDetail.gtv && animationUrl
 *     ? <div class="banner scorestats mb-1"
 *            style="background-image:url(<img>/front/img/events-banner/<etid>.png)">
 *         <iframe :src="animationUrl" frameborder="0"></iframe>
 *       </div>
 *     : nothing
 *
 * and the url is minted per match:
 *   0 != gameDetail.gtv && callApi('casino/gettoken', {id:'graphic'})
 *     .then(r => animationUrl = `${r.data.url}/${gameDetail.gtv}/2`)
 *
 * `gtv` is the feed's graphic id — 0 means the match has no animation at all.
 * It is NOT in `cricket/all-data`; it comes from
 * `/user/v1/sports-gamedetailPrivate` (the page polls that for the header).
 *
 * Our server already does the token dance for the player site
 * (`GET /user/cricket/score-url` → the provider url, returned UNUSED), so the
 * same endpoint feeds this. The token is SINGLE USE:
 *   • the url is fetched ONCE per gmid and frozen — a re-render must never
 *     mint a second one (the component is memoised for that reason too),
 *   • only one mounted copy per gmid owns the iframe,
 *   • never "check" the url before using it — the check would be the one use.
 * Ported from frontend/src/components/gamedetails/SportsScorecard.jsx, where
 * this was worked out (docs/handover/2026-09-08-scorecard-404-fix.md).
 *
 * The background art is the reference's CDN sheet, the same host this page
 * already uses for the accordion arrows. If the url does not resolve (the
 * host answers 502 for matches it has no graphic for) the box still paints
 * its art, exactly as it does while the url is in flight.
 */

const BANNER_BASE = 'https://wver.sprintstaticdata.com/v230/static/front/img/events-banner/'
// Optional direct fallback to the scorecard host (the player site ships it as
// VITE_SCORECARD_BASE). Unset in admin/.env → no fallback, art only.
const SCORECARD_BASE = import.meta.env.VITE_SCORECARD_BASE || ''

// gmid -> the instance id that owns the iframe
const owners = new Map()
let nextId = 0

function SportsScorecard({ gmid, etid }) {
  const idRef = useRef(null)
  if (idRef.current === null) idRef.current = ++nextId

  const [owns, setOwns] = useState(false)
  const [scoreUrl, setScoreUrl] = useState(null)
  const requestedFor = useRef(null)
  const key = String(gmid || '')

  useEffect(() => {
    if (!key) { setOwns(false); return undefined }
    const me = idRef.current
    if (!owners.has(key)) owners.set(key, me)
    setOwns(owners.get(key) === me)
    return () => { if (owners.get(key) === me) owners.delete(key) }
  }, [key])

  useEffect(() => {
    if (!owns || !key || !etid) return undefined
    if (requestedFor.current === key) return undefined   // already asked for this match
    requestedFor.current = key

    const fallback = SCORECARD_BASE ? `${SCORECARD_BASE}?etid=${etid}&gmid=${key}` : null
    let alive = true
    getScoreUrl(key, etid)
      .then((url) => { if (alive) setScoreUrl(url || fallback) })
      .catch(() => { if (alive) setScoreUrl(fallback) })
    return () => { alive = false }
  }, [owns, key, etid])

  return (
    <div
      className="banner scorestats mb-1"
      style={etid ? { backgroundImage: `url("${BANNER_BASE}${etid}.png")` } : undefined}
    >
      {owns && scoreUrl && <iframe src={scoreUrl} title="Live Score Card" frameBorder="0" />}
    </div>
  )
}

/* Memoised: the page re-renders on every odds poll (2s) and every meta poll
   (5s); a re-render that recreated the iframe would spend another token. */
export default memo(SportsScorecard)
